import { Popover } from "@base-ui-components/react/popover"
import cx from "classnames"
import { Check, ChevronDown, Image, Search } from "lucide-react"
import { useEffect, useState } from "react"
import { observer } from "mobx-react"
import { DEFAULT_HARNESS_ID } from "../constants"
import { formatModelPricing, matchesModelSearch } from "../modelCatalog"
import type { HarnessId } from "../providerEvents/harnessEventTypes"
import { getVisibleModelEntries, getVisibleModelId } from "../modelVisibility"
import { usePortalContainer } from "../hooks/usePortalContainer"
import { Select } from "./ui/Select"

interface ModelPickerProps {
    value: string
    onChange: (modelId: string) => void
    harnessId?: HarnessId
    touchFriendly?: boolean
    side?: "top" | "bottom" | "left" | "right"
    align?: "start" | "center" | "end"
}

export const ModelPicker = observer(function ModelPicker({
    value,
    onChange,
    harnessId = DEFAULT_HARNESS_ID,
    touchFriendly = false,
    side = "top",
    align = "end",
}: ModelPickerProps) {
    const models = getVisibleModelEntries(harnessId)
    const entries = models.map((model) => ({
        id: model.id,
        content: model.label,
    }))
    const visibleValue = getVisibleModelId(value, harnessId)

    useEffect(() => {
        if (visibleValue !== value) {
            onChange(visibleValue)
        }
    }, [visibleValue, value, onChange])

    if (models.length > 12) {
        return <SearchableModelPicker models={models} value={visibleValue} onChange={onChange} touchFriendly={touchFriendly} side={side} align={align} />
    }

    return (
        <Select
            selectedId={visibleValue}
            entries={entries}
            onSelect={(entry) => onChange(entry.id)}
            noArrow
            side={side}
            align={align}
            className={{
                trigger: touchFriendly
                    ? "h-11 max-w-full px-3 text-sm font-mono text-muted border border-border bg-transparent hover:bg-base-200"
                    : "h-7 max-w-24 px-2 text-xs font-mono text-muted border-0 bg-transparent hover:bg-base-200",
                value: touchFriendly ? "truncate text-sm" : "truncate text-xs",
            }}
        />
    )
})

function SearchableModelPicker({
    models,
    value,
    onChange,
    touchFriendly,
    side,
    align,
}: {
    models: ReturnType<typeof getVisibleModelEntries>
    value: string
    onChange: (modelId: string) => void
    touchFriendly: boolean
    side: "top" | "bottom" | "left" | "right"
    align: "start" | "center" | "end"
}) {
    const portalContainer = usePortalContainer()
    const [open, setOpen] = useState(false)
    const [query, setQuery] = useState("")
    const selected = models.find((model) => model.id === value)
    const filtered = models.filter((model) =>
        matchesModelSearch(query, [model.id, model.fullId, model.selector, model.label, model.displayClass, model.class, model.nativeProvider])
    )
    return (
        <Popover.Root
            open={open}
            onOpenChange={(nextOpen) => {
                setOpen(nextOpen)
                if (!nextOpen) setQuery("")
            }}
        >
            <Popover.Trigger
                aria-label="Choose model"
                className={cx(
                    "btn flex items-center justify-between gap-2 border border-border bg-transparent font-mono text-muted hover:bg-base-200",
                    touchFriendly ? "h-11 max-w-full px-3 text-sm" : "h-7 max-w-28 border-0 px-2 text-xs"
                )}
            >
                <span className="truncate">{selected?.label ?? value}</span>
                <ChevronDown size={13} className="shrink-0" />
            </Popover.Trigger>
            <Popover.Portal container={portalContainer}>
                <Popover.Positioner side={side} align={align} sideOffset={8} className="z-50">
                    <Popover.Popup className="w-[min(28rem,calc(100vw-1rem))] border border-border bg-base-100 shadow-lg outline-none">
                        <label className="m-3 flex h-9 items-center gap-2 border border-border bg-base-200 px-3 text-muted focus-within:border-primary">
                            <Search size={14} aria-hidden="true" />
                            <input
                                type="search"
                                value={query}
                                onChange={(event) => setQuery(event.target.value)}
                                placeholder={`Search ${models.length} models`}
                                aria-label="Search models"
                                className="min-w-0 flex-1 appearance-none border-0 bg-transparent p-0 text-sm text-base-content outline-none placeholder:text-muted"
                            />
                        </label>
                        <div className="max-h-[min(28rem,60dvh)] overflow-y-auto border-t border-border py-1">
                            {filtered.map((model) => {
                                const pricing = formatModelPricing(model.pricing)
                                return (
                                    <button
                                        key={model.id}
                                        type="button"
                                        className="btn flex min-h-11 w-full items-center gap-2 px-3 py-2 text-left hover:bg-base-200"
                                        onClick={() => {
                                            onChange(model.id)
                                            setOpen(false)
                                            setQuery("")
                                        }}
                                    >
                                        <span className="w-4 shrink-0">{model.id === value && <Check size={13} />}</span>
                                        <span className="min-w-0 flex-1">
                                            <span className="block truncate text-sm text-base-content">{model.label}</span>
                                            <span className="block truncate text-[11px] text-muted">
                                                {[model.nativeProvider, model.displayClass, pricing].filter(Boolean).join(" · ")}
                                            </span>
                                        </span>
                                        {model.capabilities?.images && <Image size={13} className="shrink-0 text-muted" aria-label="Supports image input" />}
                                    </button>
                                )
                            })}
                            {filtered.length === 0 && <div className="px-3 py-8 text-center text-sm text-muted">No models match “{query}”.</div>}
                        </div>
                    </Popover.Popup>
                </Popover.Positioner>
            </Popover.Portal>
        </Popover.Root>
    )
}
